"use client";

import type { Variants } from "motion/react";
import { motion, useAnimation } from "motion/react";
import type { HTMLAttributes } from "react";
import React, { forwardRef, useCallback, useImperativeHandle } from "react";

import { cn } from "@/lib/utils";

export interface TrashIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface TrashIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
}

const BIN_VARIANTS: Variants = {
  normal: { y: 0 },
  animate: {
    y: [0, -1.5, 0],
    transition: {
      duration: 0.26,
      ease: "easeInOut",
    },
  },
};

const LID_VARIANTS: Variants = {
  normal: { rotate: 0, y: 0 },
  animate: {
    rotate: -12,
    y: -1,
    transition: {
      type: "spring",
      stiffness: 260,
      damping: 14,
      mass: 0.9,
    },
  },
};

const TrashIcon = forwardRef<TrashIconHandle, TrashIconProps>(({ onMouseEnter, onMouseLeave, className, size = 28, ...props }, ref) => {
  const controls = useAnimation();

  useImperativeHandle(ref, () => {
    return {
      startAnimation: () => {
        void controls.start("animate");
      },
      stopAnimation: () => {
        void controls.start("normal");
      },
    };
  });

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onMouseEnter?.(e);
      void controls.start("animate");
    },
    [controls, onMouseEnter]
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onMouseLeave?.(e);
      void controls.start("normal");
    },
    [controls, onMouseLeave]
  );

  return (
    <div className={cn(className)} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} {...props}>
      <svg
        fill="none"
        height={size}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
        width={size}
        xmlns="http://www.w3.org/2000/svg"
      >
        <motion.g animate={controls} initial="normal" variants={LID_VARIANTS} style={{ transformOrigin: "9px 8px" }}>
          <path d="M3 6h18" />
          <path d="M8 6V4h8v2" />
        </motion.g>
        <motion.g animate={controls} initial="normal" variants={BIN_VARIANTS}>
          <path d="M6 6l1 14h10l1-14" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
        </motion.g>
      </svg>
    </div>
  );
});

TrashIcon.displayName = "TrashIcon";

export { TrashIcon };
